import { transport } from "@/src/config/apiConfig.js";
import { convertToApiSlug } from "../utils/crmFilterUtils";
import { crmTransport } from "@/src/config/crmTransport.js";
import { viewsTransport } from "@/src/config/viewsTransport.js";

export default {
  async fetchAllowedObjects() {
    try {
      let res = await transport.get(`/api/crm/v2/allowed_objects`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching allowed objects: ",
        error,
      );
      throw error;
    }
  },

  async fetchDynamicCRMListObjects(service = null) {
    try {
      const params = service ? { service } : {};
      let res = await crmTransport.get(`/schema/objects`, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic CRM list objects: ",
        error,
      );
      throw error;
    }
  },

  async fetchSchemaObject(slug, service = null) {
    try {
      const apiSlug = convertToApiSlug(slug);
      const params = service ? { service } : {};
      console.log("[CRMService.fetchSchemaObject]", {
        slug,
        apiSlug,
        service,
        willSendServiceQuery: !!service,
      });
      let res = await crmTransport.get(`/schema/objects/${apiSlug}`, { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching schema object: ", error);
      throw error;
    }
  },

async fetchDynamicEntityViews(slug, entityId = null, service = null) {
    try {
      const params = { entity_type: slug };
      if (entityId) params.entity_id = entityId;
      if (service) params.service = service;
      let res = await viewsTransport.get(`/views`, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic entity views: ",
        error,
      );
      throw error;
    }
  },

  async fetchDynamicEntityData(
    slug,
    viewId,
    page,
    pageSize,
    searchQuery = "",
    subObjectId = null,
    config = {},
    lastDocumentId = null,
    firstDocumentId = null,
    service = null,
  ) {
    try {
      const params = new URLSearchParams({
        page_size: pageSize,
        page: page,
      });

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search_query", searchQuery);
      }

      if (subObjectId) {
        params.append("sub_object_type_id", subObjectId);
      }

      if (lastDocumentId) {
        params.append("last_document_id", lastDocumentId);
      }

      if (firstDocumentId) {
        params.append("first_document_id", firstDocumentId);
      }

      if (service) {
        params.append("service", service);
      }

      let res = await transport.get(
        `/api/crm/v2/views/${viewId}/results?${params}`,
        config,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic entity data: ",
        error,
      );
      throw error;
    }
  },

  async fetchKanbanResults(
    viewId,
    {
      page = 1,
      pageSize = 10,
      option = null,
      searchQuery = "",
      subObjectId = null,
      service = null,
    } = {},
    config = {},
  ) {
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
      });

      if (option) {
        params.append("option", option);
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search_query", searchQuery);
      }

      if (subObjectId) {
        params.append("sub_object_type_id", subObjectId);
      }

      if (service) {
        params.append("service", service);
      }

      let res = await transport.get(
        `/api/crm/v2/views/${viewId}/results/kanban?${params}`,
        config,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching kanban results: ", error);
      throw error;
    }
  },

  async deleteDynamicEntity(slug, entityId, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await transport.delete(
        `/api/crm/v2/objects/${slug}/${entityId}`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting dynamic entity: ", error);
      throw error;
    }
  },

  async fetchDynamicEntityDetails(slug, entityId, service = null) {
    try {
      const params = {};
      if (service) params.service = service;
      let res = await transport.get(`/api/crm/v2/objects/${slug}/${entityId}`, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic entity details: ",
        error,
      );
      throw error;
    }
  },

  async getAssociatedEntity(objectType, objectId) {
    try {
      let res = await transport.get(
        `/api/crm/v2/objects/${objectType}/${objectId}/associated-entity`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching associated entity: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityChildren(slug, entityId, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await crmTransport.get(`/objects/${slug}/${entityId}/children`, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching entity children: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityParentHierarchy(slug, entityId) {
    try {
      let res = await crmTransport.get(`/objects/${slug}/${entityId}/parents`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching entity parent hierarchy: ",
        error,
      );
      throw error;
    }
  },

  async addDynamicEntityNote(slug, entityId, noteData) {
    try {
      let res = await transport.post(
        `/api/crm/v2/${slug}/${entityId}/notes`,
        noteData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while adding note to dynamic entity: ",
        error,
      );
      throw error;
    }
  },

  async fetchDynamicEntityNotes(slug, entityId) {
    try {
      let res = await transport.get(`/api/${slug}/${entityId}/notes`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic entity notes: ",
        error,
      );
      throw error;
    }
  },

  async fetchDynamicEntityActivityLogs(
    entityType,
    entityId,
    activityType,
    page = 0,
    activityTypesFilter = null,
    createdById = null,
    createdDate = null,
    formIds = null,
    service = null,
  ) {
    try {
      let res = await transport.get(
        `/api/crm/v2/${entityType}/${entityId}/activities`,
        {
          params: {
            activity_type: activityType,
            page: page,
            activity_types_filter: activityTypesFilter,
            created_by_id: createdById,
            created_date: createdDate,
            form_ids: formIds,
            ...(service ? { service } : {}),
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching dynamic entity activities: ",
        error,
      );
      throw error;
    }
  },

  async createDynamicEntityActivity(objectType, objectId, engagementId, engagementType, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await transport.post(
        `/api/crm/v2/${objectType}/${objectId}/activities`,
        {
          engagement_id: engagementId,
          engagement_type: engagementType,
        },
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating entity activity: ", error);
      throw error;
    }
  },

  async createDynamicEntity(slug, entityData, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await transport.post(`/api/crm/v2/objects/${slug}`, {
        object: {
          attributes: entityData.attributes,
          relations: entityData.relations,
          ...(entityData.parent_id !== undefined && {
            parent_id: entityData.parent_id,
          }),
        },
      }, { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating dynamic entity: ", error);
      throw error;
    }
  },

  async updateDynamicEntity(slug, entityId, entityData, service = null) {
    try {
      const payload = entityData.object
        ? entityData
        : {
            object: {
              attributes: entityData.attributes || {},
              relations: entityData.relations || {},
              ...(entityData.parent_id !== undefined && {
                parent_id: entityData.parent_id,
              }),
            },
          };

      const params = {};
      if (service) params.service = service;
      let res = await transport.patch(
        `/api/crm/v2/objects/${slug}/${entityId}`,
        payload,
        { params },
      );

      return res.data;
    } catch (error) {
      console.error("An error occurred while updating dynamic entity: ", error);
      throw error;
    }
  },

  async bulkCreateForEntities(slug, payload, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await transport.post(
        `/api/crm/v2/objects/${slug}/bulk-create-for-entities`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while bulk creating entities: ", error);
      throw error;
    }
  },

  async getTagsForEntity(entitySlug) {
    try {
      let res = await transport.get(`/api/tags/${entitySlug}`);
      return res.data;
    } catch (error) {
      console.error(
        `An error occurred while executing getTagsForEntity for ${entitySlug}: `,
        error,
      );
      throw error;
    }
  },

  async createSchemaObjectAttribute(objectId, payload, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await crmTransport.post(
        `/schema/objects/${objectId}/attributes`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating schema object attribute: ",
        error,
      );
      throw error;
    }
  },

  async updateSchemaObjectAttribute(objectId, attributeId, payload, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await crmTransport.put(
        `/schema/objects/${objectId}/attributes/${attributeId}`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating schema object attribute: ",
        error,
      );
      throw error;
    }
  },

  async updateNote(id, payload) {
    try {
      const res = await transport.put(`/api/crm/v2/notes/${id}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing update note: ", error);
      throw error;
    }
  },

  async deleteNote(id) {
    try {
      const res = await transport.delete(`/api/crm/v2/notes/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing delete note: ", error);
      throw error;
    }
  },

  async getRelationshipConnections(params) {
    try {
      const {
        object_type,
        object_id,
        related_object_type,
        page,
        page_size,
        service,
        ...otherParams
      } = params;
      const queryParams = {
        ...(page && { page }),
        ...(page_size && { page_size }),
        ...(service && { service }),
        ...otherParams,
      };

      const res = await transport.get(
        `/api/crm/v2/${object_type}/${object_id}/relationships/${related_object_type}`,
        { params: queryParams },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while getting relationships: ", error);
      throw error;
    }
  },

  async createRelationshipConnection(payload, service = null) {
    try {
      const {
        object_type,
        object_id,
        related_object_type,
        related_object_id,
        relationship_id,
      } = payload;

      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/objects/${object_type}/${object_id}/relationships`,
        {
          relationship_id,
          target_object_id: related_object_id,
          target_object_type_id: related_object_type,
        },
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating relationships: ", error);
      throw error;
    }
  },

  async bulkCreateRelationships(payload, service = null) {
    try {
      const { object_type, object_id, ...requestBody } = payload;
      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/objects/${object_type}/${object_id}/relationships/bulk`,
        requestBody,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating relationships in bulk: ",
        error,
      );
      throw error;
    }
  },

  async deleteRelationshipConnection(id, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.delete(`/objects/relationships/${id}`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting relationship: ", error);
      throw error;
    }
  },

  async deleteSchemaAttribute(objectSlug, attributeSlug, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.delete(
        `/schema/objects/${objectSlug}/attributes/${attributeSlug}`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting schema attribute: ",
        error,
      );
      throw error;
    }
  },

  async deleteSchemaRelationship(objectSlug, relationshipId, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.delete(
        `/schema/objects/${objectSlug}/relationships/${relationshipId}`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting schema relationship: ",
        error,
      );
      throw error;
    }
  },

  async getPraticularAttributeDetails(objectId, attributeId, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.get(
        `/schema/objects/${objectId}/attributes/${attributeId}`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting praticular attribute details: ",
        error,
      );
      throw error;
    }
  },

  async updateSchemaAttributeMetadata(objectSlug, attributeSlug, payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.put(
        `/schema/objects/${objectSlug}/attributes/${attributeSlug}`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating schema attribute metadata: ",
        error,
      );
      throw error;
    }
  },

  async getParticularAttributeOptions(objectId, attributeId) {
    try {
      const res = await crmTransport.get(
        `/schema/objects/${objectId}/attributes/${attributeId}/options`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting praticular attribute options: ",
        error,
      );
      throw error;
    }
  },

  async editAttributeOption(optionId, payload, attributeId, service = null) {
    try {
      const url = attributeId
        ? `/schema/options/${optionId}?attribute_id=${attributeId}`
        : `/schema/options/${optionId}`;
      const params = service ? { service } : {};
      const res = await crmTransport.put(url, payload, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while editing attribute option: ",
        error,
      );
      throw error;
    }
  },

  async addAttributeOption(objectId, attributeId, payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/schema/objects/${objectId}/attributes/${attributeId}/options/bulk`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while adding attribute option: ", error);
      throw error;
    }
  },

  async deleteAttributeOption(optionId, attributeId, service = null) {
    try {
      const url = attributeId
        ? `/schema/options/${optionId}?attribute_id=${attributeId}`
        : `/schema/options/${optionId}`;
      const params = service ? { service } : {};
      const res = await crmTransport.delete(url, { params });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting attribute option: ",
        error,
      );
      throw error;
    }
  },

  async createCustomObject(payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/schema/objects`,
        payload.object.attributes,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating custom object: ", error);
      throw error;
    }
  },

  async updateCustomObject(slug, payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.patch(
        `/schema/objects/${slug}`,
        {
          name: payload.name,
          plural_name: payload.plural,
          display_prefix: payload.display_prefix,
        },
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating custom object: ", error);
      throw error;
    }
  },

  async deleteCustomObject(slug, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.delete(`/schema/objects/${slug}`, { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting custom object: ", error);
      throw error;
    }
  },

  async createAssociation(objectType, payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/schema/objects/${objectType}/relationships`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating association: ", error);
      throw error;
    }
  },

  async upsertRelationshipCardinality(payload, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await transport.put(
        `/api/crm/v2/relationship-cardinalities/upsert`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while upserting relationship cardinality: ",
        error,
      );
      throw error;
    }
  },

  async updateRelationshipCardinality(relationshipId, cardinality, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await transport.patch(
        `/api/crm/v2/schema/relationships/${relationshipId}/cardinality`,
        { cardinality },
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating relationship cardinality: ",
        error,
      );
      throw error;
    }
  },

  async getForm(objectSlug, service = null) {
    try {
      const apiSlug = convertToApiSlug(objectSlug);
      const params = service ? { service } : {};
      let res = await transport.get(
        `/api/crm/v2/schema/objects/${apiSlug}/forms`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching form: ", error);
      throw error;
    }
  },

  async updateForm(objectSlug, attributes, service = null) {
    try {
      const apiSlug = convertToApiSlug(objectSlug);
      const params = service ? { service } : {};
      let res = await transport.put(
        `/api/crm/v2/schema/objects/${apiSlug}/forms`,
        {
          attributes: attributes,
        },
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating form: ", error);
      throw error;
    }
  },

  async searchDynamicObjects(params, config = {}) {
    try {
      const { object_type, search_query, page, page_size, service, ...otherParams } =
        params;

      const searchParams = {
        q: search_query || "",
        page: page || 1,
        page_size: page_size || 10,
        ...otherParams,
      };
      if (service) searchParams.service = service;

      let res = await transport.get(
        `/api/crm/v2/objects/${object_type}/search`,
        {
          params: searchParams,
          ...config,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while searching dynamic objects: ",
        error,
      );
      throw error;
    }
  },

  async searchMultiObjects(payload, config = {}) {
    try {
      const res = await transport.post(
        `/api/objects/search/multi`,
        payload,
        config,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while searching across multiple objects: ",
        error,
      );
      throw error;
    }
  },

  async searchAllFields(params, config = {}) {
    try {
      const { object_type, search_query, page, page_size, service, ...otherParams } =
        params;

      const searchParams = {
        q: search_query || "",
        page: page || 1,
        page_size: page_size || 10,
        ...otherParams,
      };

      if (service) searchParams.service = service;
      let res = await transport.get(
        `/api/crm/v2/objects/${object_type}/search-all-fields`,
        {
          params: searchParams,
          ...config,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while searching all fields: ", error);
      throw error;
    }
  },

  async checkDuplicateRecord(
    objectType,
    attributeSlug,
    value,
    excludeId = null,
    service = null,
  ) {
    try {
      const payload = {
        attributes: {
          [attributeSlug]: value,
        },
      };

      if (excludeId) {
        payload.record_id = excludeId;
      }

      const params = service ? { service } : {};
      const res = await crmTransport.post(
        `/objects/${objectType}/check-uniqueness`,
        payload,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while checking for duplicate record: ",
        error,
      );
      throw error;
    }
  },

  async getObjectsWithRelationships(payload) {
    try {
      const res = await crmTransport.post(
        `/objects/relationships/objects-with-relationships`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching objects with relationships: ",
        error,
      );
      throw error;
    }
  },

  async bulkUpdateByIds(objectType, payload, service = null) {
    try {
      const { record_ids, updates, update_type } = payload;
      const queryParams = service ? { service } : {};
      const res = await crmTransport.patch(
        `/objects/${objectType}/bulk-update-by-ids`,
        {
          record_ids,
          attributes: updates,
          ...(update_type && { update_type }),
        },
        { params: queryParams },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while bulk updating by IDs: ", error);
      throw error;
    }
  },

  async bulkDeleteByIds(objectType, payload, service = null) {
    try {
      const queryParams = service ? { service } : {};
      const res = await crmTransport.delete(
        `/objects/${objectType}/bulk-delete-by-ids`,
        {
          data: payload,
          params: queryParams,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while bulk deleting by IDs: ", error);
      throw error;
    }
  },

  async updateMultiple(objectType, payload, service = null) {
    try {
      const { updates, unselected_ids, update_type, filters, search_query } = payload;

      const params = new URLSearchParams();
      params.append('filters', JSON.stringify(filters || {}));

      if (unselected_ids && unselected_ids.length > 0) {
        params.append('unselected_ids', JSON.stringify(unselected_ids));
      }

      if (update_type) {
        params.append('update_type', update_type);
      }

      if (search_query) {
        params.append('search_query', search_query);
      }

      if (service) {
        params.append('service', service);
      }

      const res = await crmTransport.patch(
        `/objects/${objectType}/update-by-criteria?${params.toString()}`,
        {
          attributes: updates,
        },
      );
      const data = res.data;
      if (data?.async) {
        return { success: true, async: true, estimated_count: data.estimated_count, message: data.message };
      }
      return data;
    } catch (error) {
      console.error(
        "An error occurred while updating multiple records: ",
        error,
      );
      throw error;
    }
  },

async deleteMultiple(objectType, payload, service = null) {
    try {
      const { unselected_ids, filters, search_query } = payload;

      const params = new URLSearchParams();
      params.append('filters', JSON.stringify(filters || {}));

      if (unselected_ids && unselected_ids.length > 0) {
        params.append('unselected_ids', JSON.stringify(unselected_ids));
      }

      if (search_query) {
        params.append('search_query', search_query);
      }

      if (service) {
        params.append('service', service);
      }
      const res = await crmTransport.delete(
        `/objects/${objectType}/delete-by-criteria?${params.toString()}`
      );
      const data = res.data;
      if (data?.async) {
        return { success: true, async: true, estimated_count: data.estimated_count, message: data.message };
      }
      return data;
    } catch (error) {
      console.error(
        "An error occurred while deleting multiple records: ",
        error,
      );
      throw error;
    }
  },

  /**
   * Bulk create objects for multiple target entities
   * Creates a new record of objectType for each selected target record
   *
   * @param {string} objectType - The type of object to create (e.g., 'crm_tasks')
   * @param {Object} payload - The bulk create payload
   * @param {string} payload.target_object_type - Target object type (e.g., 'leads', 'contacts')
   * @param {string} payload.relationship_id - Relationship ID to link new records to targets
   * @param {Array} payload.selected_ids - Specific target record IDs (when not using select_all)
   * @param {boolean} payload.select_all - Whether to use all filtered records
   * @param {string} payload.view_id - View ID for filtering (required with select_all)
   * @param {Array} payload.unselected_ids - IDs to exclude when using select_all
   * @param {Object} payload.attributes - Attributes for the new records
   * @returns {Promise<Object>} Response with success status and target count
   */
  async bulkCreateForEntities(objectType, payload) {
    try {
      const res = await transport.post(
        `/api/crm/v2/objects/${objectType}/bulk-create-for-entities`,
        {
          target_object_type: payload.target_object_type,
          relationship_id: payload.relationship_id,
          selected_ids: payload.selected_ids,
          select_all: payload.select_all,
          view_id: payload.view_id,
          list_id: payload.list_id,
          unselected_ids: payload.unselected_ids,
          search_query: payload.search_query,
          object: {
            attributes: payload.attributes || {},
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while bulk creating for entities: ",
        error,
      );
      throw error;
    }
  },

  // Sub-Objects APIs
  async createSubObject(objectSlug, payload) {
    try {
      const res = await transport.post(
        `/api/crm/v2/schema/objects/${objectSlug}/sub-objects`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating sub-object: ", error);
      throw error;
    }
  },

  async getSubObject(slug) {
    try {
      const res = await transport.get(`/api/crm/v2/schema/sub-objects/${slug}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching sub-object: ", error);
      throw error;
    }
  },

  async listSubObjectsByObject(objectSlug, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await transport.get(
        `/api/crm/v2/schema/objects/${objectSlug}/sub-objects`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while listing sub-objects by object: ",
        error,
      );
      throw error;
    }
  },

  async listSubObjectsByParent(objectSlug, parentId) {
    try {
      const res = await transport.get(
        `/api/crm/v2/schema/objects/${objectSlug}/sub-objects/parent/${parentId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while listing sub-objects by parent: ",
        error,
      );
      throw error;
    }
  },

  async updateSubObject(slug, payload) {
    try {
      const res = await transport.patch(
        `/api/crm/v2/schema/sub-objects/${slug}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating sub-object: ", error);
      throw error;
    }
  },

  async deleteSubObject(slug, service = null) {
    try {
      const params = service ? { service } : {};
      const res = await transport.delete(
        `/api/crm/v2/schema/sub-objects/${slug}`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting sub-object: ", error);
      throw error;
    }
  },

  // Sub-Object Record Relationship APIs

  async getParentRecords(
    objectSlug,
    recordId,
    { page = 1, page_size = 10 } = {},
  ) {
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: page_size,
      });

      const res = await transport.get(
        `/api/crm/v2/objects/${objectSlug}/${recordId}/parent-records?${params}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching parent records: ", error);
      throw error;
    }
  },

  async getChildRecords(
    objectSlug,
    recordId,
    { page = 1, page_size = 10 } = {},
  ) {
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: page_size,
      });

      const res = await transport.get(
        `/api/crm/v2/objects/${objectSlug}/${recordId}/child-records?${params}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching child records: ", error);
      throw error;
    }
  },

  async createSubObjectRelationship(payload) {
    try {
      const res = await transport.post(
        `/api/crm/v2/objects/sub-object-relationships`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating sub-object relationship: ",
        error,
      );
      throw error;
    }
  },

  async bulkCreateSubObjectRelationships(payload) {
    try {
      const res = await transport.post(
        `/api/crm/v2/objects/sub-object-relationships/bulk`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while bulk creating sub-object relationships: ",
        error,
      );
      throw error;
    }
  },

  async deleteSubObjectRelationship(relationshipId) {
    try {
      const res = await transport.delete(
        `/api/crm/v2/objects/sub-object-relationships/${relationshipId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting sub-object relationship: ",
        error,
      );
      throw error;
    }
  },

  async bulkDeleteSubObjectRelationships(relationshipIds) {
    try {
      const res = await transport.post(
        `/api/crm/v2/objects/sub-object-relationships/bulk-delete`,
        { relationship_ids: relationshipIds },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while bulk deleting sub-object relationships: ",
        error,
      );
      throw error;
    }
  },

  async generateUploadUrl(files) {
    try {
      const res = await transport.post("/api/objects/attachments/upload-url", {
        files,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while generating upload URL: ", error);
      throw error;
    }
  },

  async confirmUpload(attachmentId) {
    try {
      const res = await transport.post(
        `/api/objects/attachments/${attachmentId}/confirm`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while confirming upload: ", error);
      throw error;
    }
  },

  async generateDownloadUrl(storageKey) {
    try {
      const res = await transport.post(
        "/api/objects/attachments/download-url",
        { storage_key: storageKey },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while generating download URL: ", error);
      throw error;
    }
  },

  async deleteAttachment(attachmentId) {
    try {
      const res = await transport.delete(
        `/api/objects/attachments/${attachmentId}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting attachment: ", error);
      throw error;
    }
  },


  async patchContact(contactId, payload) {
    try {
      const res = await transport.patch(`/api/contacts/${contactId}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while patching contact: ", error);
      throw error;
    }
  },

  async patchLead(leadId, payload) {
    try {
      const res = await transport.patch(`/api/leads/${leadId}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while patching lead: ", error);
      throw error;
    }
  },

  async patchCompany(companyId, payload) {
    try {
      const res = await transport.patch(`/api/companies/${companyId}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while patching company: ", error);
      throw error;
    }
  },

  async patchDeal(dealId, payload) {
    try {
      const res = await transport.patch(`/api/deals/${dealId}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while patching deal: ", error);
      throw error;
    }
  },

  async postContact(payload) {
    try {
      const res = await transport.post("/api/contacts", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating contact: ", error);
      throw error;
    }
  },

  async postLead(payload) {
    try {
      const res = await transport.post("/api/leads", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating lead: ", error);
      throw error;
    }
  },

  async postCompany(payload) {
    try {
      const res = await transport.post("/api/companies", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating company: ", error);
      throw error;
    }
  },

  async postDeal(payload) {
    try {
      const res = await transport.post("/api/deals", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating deal: ", error);
      throw error;
    }
  },
};
